import {
  AllowedModule,
  CheckOutFlows,
  ItemEntitlements,
  PaymentDTO,
  PaymentType,
  PaymentGatewayType,
  ScheduledPaymentDTO,
} from '@rewaa-team/types';
import { SecondaryIDEnum } from '@rewaa-team/rewaa-common';
import { LinkedPayment } from 'chargebee-typescript/lib/resources/invoice';
import { CouponApplicabilityStatus, ShippingAddressTypes } from './constants';
// eslint-disable-next-line import/no-cycle
import { CbCreditNoteData, ZatcaMessage } from '../shared/types';

export interface CrmInfo {
  monshaatOrderNumber?: string;
  monshaatCompanyName?: string;
  monshaatCommercialRegistration?: string;
}

export interface AccountDTO {
  id: number;
  email: string;
  companyName: string;
  schemaName: string;
  status: string;
  phone: string;
  subscriptionStatus?: string;
  lastInvoiceDate?: number;
  lastInvoiceFormattedDate?: string;
}

export interface BlockedMonshaatOrder {
  orderId: string;
  createdAt: number;
  formattedDate: string;
}

export interface CursorAccountListDTO {
  accountList: AccountDTO[];
  hasNextPage: boolean;
  nextCursor: number | null;
}

export interface PaginatedAccountListDTO {
  accountList: AccountDTO[];
  totalRecords: number;
}

export interface InstalledApp {
  id: number;
  name: string;
}

export interface AllowedApp {
  featureId: string;
  featureName: string;
  isPlanEntitlement: boolean;
}

export interface AccountDetailsDTO {
  merchant: MerchantDetail;
  subscription: SubscriptionDetail | undefined;
  crm: CrmDetails;
  paymentGatewaySubscriptions?: PaymentGatewaySubscriptionDetail[];
}

export interface MerchantDetail {
  id: number;
  tenantId?: number;
  status: string;
  email: string;
  companyName: string;
  companyType: string;
  companyAddress: string;
  phoneNumber: string;
  schemaName: string;
  vatNumber: string;
  crNumber: string;
  activeLocationCount: number;
  numberOfUsedSKUs?: number;
  accountCreationDate: Date | string;
  lastLogin: string;
  excessPayment: number;
  users: MerchantUserWithRole[];
  resetPasswordCode?: ResetPasswordCode;
}

export interface MerchantRole {
  id: number;
  name: string;
}

export interface MerchantUserWithRole {
  id: number;
  userName: string;
  role: MerchantRole[];
  email: string;
  status: string;
  lastLogin: Date | null;
}

export interface CrmDetails {
  accountId: string;
  email: string;
  onboardingStatus?: string;
  leadOwner?: string;
  leadSource?: string;
  publisher?: string;
  channel?: string;
  referralEmail?: string;
  csOwner?: string;
  isChurned?: boolean | string;
  churnDate?: unknown;
  refundDate?: unknown;
  phoneNumber?: string;
  companyName?: string;
  yearMonshaatServiceOffer?: boolean;
  isGrubtechEligible?: boolean;
}

export interface SubscriptionDetail {
  subscriptionId: string;
  subscriptionStartDate: unknown;
  planName: string;
  planId: string;
  customerId: string;
  activeUsersCount: number;
  allowedApps: AllowedApp[];
  installedApps: InstalledApp[];
  subscriptionStatus: string;
  nextBillingDate: unknown;
  nextBillingAmount: number;
  allowedCustomFields: number;
  allowedLocations: number;
  period?: number;
  excessPayment: number;
  basePlanId?: string;
}

export interface PaymentGatewaySubscriptionDetail {
  subscriptionId: string;
  subscriptionStatus: string;
  nextBillingDate: string;
  nextBillingAmount: number;
  gatewayType: PaymentGatewayType;
}

export interface BillingDocumentLineItem {
  id: string;
  itemId?: string;
  type: string;
  name: string;
  quantity: number;
  unitAmount: number;
  amount: number;
  taxAmount: number;
  taxRate?: number;
  discountAmount?: number;
  period?: number;
  unit?: string;
  taxableAmount?: number;
  dateFrom?: number;
  dateTo?: number;
  internalName?: string;
  externalName?: string;
}

export interface LineItemSelection {
  id: string;
  quantity: number;
}

export interface CreditNoteRequest {
  reference_invoice_id: string;
  plan_id?: string;
  date?: number;
  create_reason_code: string;
  customer_notes: string;
  isProratedCreditNote?: boolean;
  selectedLineItemIds?: string[];
  selectedLineItems?: LineItemSelection[];
}

export interface CreditNoteRequestForPartiallyPaidInvoice
  extends CreditNoteRequest {
  creditNoteType: string;
}

export interface SelectableLineItem extends BillingDocumentLineItem {
  selected?: boolean;
  selectedQuantity?: number;
  availableQuantity?: number;
}

export interface MappedBillingDocument {
  id: string;
  subscriptionId?: string;
  type: string;
  status: string;
  vatNumber?: string;
  date: number;
  dueDate?: number;
  paidAt?: number;
  total?: number;
  subTotal: number;
  tax: number;
  amountPaid: number;
  amountAdjusted?: number;
  creditsApplied?: number;
  dueAmount?: number;
  lineItems: BillingDocumentLineItem[];
  billingAddress?: BillingAddress;
  desc?: string;
  isCreditNote: boolean;
  negativeSymbol?: string;
  companyVat?: string;
  discount?: number;
  saleType?: string;
  receiptType?: string;
  zatcaStatus?: string;
  statusCode?: number;
  zatcaError?: ZatcaMessage[];
  formattedDate?: string;
  reference_invoice_id?: string;
  invoice_type?: string | null;
  credit_note_type?: string;
  errorBlob?: object;
  credit_note_visibility?: boolean;
  adjustment_credit_notes?: CbCreditNoteData[];
  issued_credit_notes?: CbCreditNoteData[];
  additionalIdType?: SecondaryIDEnum;
  additionalIdValue?: string;
  createdAt?: Date;
  webhookTimestamp?: string;
  zatcaQrCode?: string;
  sequencedId?: string;
  oldSequencedId?: string;
  zatcaWarning?: any;
  sequencedReferenceId?: string;
  sequencedOldReferenceId?: string;
  email?: string;
  customerId?: string;
  phoneNumber?: string;
  monshaatOrderNumber?: string;
  monshaatCompanyName?: string;
  crmInfo?: CrmInfo;
  currency: string;
  netTermDays?: number;
}

export interface IssueCreditNoteDTO {
  subtotal: number;
  discount: number;
  promotions: number;
  tax: number;
  total: number;
}

export interface CreditNoteLineItemDTO {
  name: string;
  quantity: number;
  adjustedPrice: number;
  totalPrice: number;
}

export interface BillingAddress {
  first_name?: string;
  last_name?: string;
  email?: string;
  company?: string;
  phone?: string;
  line1?: string;
  line2?: string;
  line3?: string;
  city?: string;
  state_code?: string;
  state?: string;
  country?: string;
  zip?: string;
  validation_status?: string;
}

export interface AttachedItem {
  id: string;
  parent_item_id: string;
  item_id: string;
  type: string;
  status?: string;
  quantity?: number;
  quantity_in_decimal?: string;
  billing_cycles?: number;
  charge_on_event: string;
  charge_once: boolean;
  created_at: number;
  resource_version?: number;
  updated_at?: number;
  channel?: string;
}

export interface UpdateMerchantResponseDTO {
  success: boolean;
  subscriptionId: string;
  subscriptionActive: boolean;
}

export interface AccountSubscriptionDetailsDTO {
  currentPlan: string;
  nextInvoiceIssueDate: Date;
  invoiceTotal: number;
  consumedLocations: number;
  consumedSKUs?: number;
  tenantId: number;
  allowedLocations: number;
  allowedFullStackAccountant: number;
  allowedFnBBundle: number;
  allowedSKUs: number;
  isLegacyPlan?: boolean;
  allowedApps: AllowedApp[];
  planShortDescriptions: string;
  attachedItems: AttachedItem[];
  addOnLocationCount: number;
  installedApps: InstalledApp[];
  subscriptionStatus: string;
  subscriptionStartDate: Date | string;
  subscriptionEndDate: Date | string;
  duration: number;
  subscriptionItems?: SubscriptionItem[] | undefined;
  detailedSubscriptionStatus: string;
  subscriptionEntitlements?: SubscriptionEntitlement[];
  allowedAddons?: AllowedModule[];
  trialEndDate?: number;
  trialStartDate?: number;
}

export interface SubscriptionEntitlement {
  featureId: string;
  featureType: FeaturePurchasingType;
  value: string;
  addon_quantity: string;
}

export enum FeaturePurchasingType {
  SWITCH = 'SWITCH',
  QUANTITY = 'QUANTITY',
}

export interface EstimateDTO {
  planId: string;
  planDuartion: number;
  locationId: string;
  locations: number;
  chargeIds: string[];
  applicationIds: string[];
}

export interface ItemPriceDetailsDTO {
  itemPriceId: string;
  unit: string;
  period: number;
  name: string;
  price: number;
}

export interface AttachedItemDTO {
  itemId: string;
  type: string;
  name?: string;
  pricingDetail?: string;
  planClassification?: PlanClassification;
  priceDetails: ItemPriceDetailsDTO[];
  crmCode?: string;
}

export enum PlanClassification {
  MonshaatPlan = 'Monshaat Plan',
  MonshaatPlanWithNewFormat = 'Monshaat Plan With New Format',
  RewaaPlan = 'Rewaa Plan',
  RewaaOldRenewalPlan = 'Rewaa Old Renewal Plan',
}

export interface PlanItemDTO {
  itemId: string;
  type: string;
  name?: string;
  planClassification?: PlanClassification;
}

export interface AttachedItemDTOWithQuantity extends AttachedItemDTO {
  quantity: number;
}

export interface SubscriptionItem {
  itemPriceId: string;
  quantity: number;
  itemType?: ItemTypes;
  unitPrice?: number;
}

export interface CheckoutEstimateReqDTO {
  subscriptionItems: SubscriptionItem[];
  couponIds: string[];
  nowInTrial: boolean;
  endOfTerm?: boolean;
  id: string;
  discountCode?: string;
  replace?: boolean;
  countryCode?: string;
  flowType: CheckOutFlows;
  isRewaapayGeideaSubscription?: boolean;
}

export interface CheckoutRenewEstimateReqDTO {
  couponIds: string[];
  id: string;
  discountCode?: string;
}

export interface PlanAttachmentsResponse {
  addons?: AttachedItemDTO[];
  charges: AttachedItemDTO[];
  includedInPlan: ItemEntitlements;
}

export interface ItemEstimate {
  itemPriceId: string;
  itemId: string;
  quantity: number;
  amount: number;
  taxAmount: number;
  discountAmount: number;
  itemLevelDiscountAmount: number;
  nextBillingAt: number;
  period: number;
  unit?: string;
  type?: ItemTypeEnum;
  externalName?: string;
  internalName?: string;
}

export interface CouponDetail {
  couponId: string;
  couponType: string;
  discountAmount: number;
  discountType?: string;
}

export interface CouponDictionary {
  [key: string]: string[];
}

export interface EstimateResponseDTO {
  estimatedItems: ItemEstimate[];
  couponDictionary?: CouponDictionary;
  totalCredits: number;
  amountPaid: number;
  promotionalCredit: number;
  invoiceLevelCouponDiscount: number;
  planType: string;
  couponApplicabilityStatus: string;
  couponDetails?: CouponDetail[];
}

export interface EstimateResponseWithCharges extends EstimateResponseDTO {
  chargesMapping?: { [itemId: string]: string };
}

export interface EstimateResponseLicenseKey {
  estimate: EstimateResponseDTO;
  shouldShippingAddressVisible: boolean;
  chargeItemDetails: { [itemId: string]: string };
}

export enum ItemTypeEnum {
  plan = 'plan',
  charge = 'charge',
  application = 'application',
  location = 'location',
  users = 'users',
  registers = 'registers',
  fullStackAccounting = 'Full-Stack-Accountant-Extra-Location',
  fbBundle = 'FB-Bundle',
  whatsAppIntegration = 'whatsapp-invoices-500-monthly',
  grubtech = 'grubtech',
  kds = 'KDS',
  whatsappInvoices1000 = 'whatsapp-invoices-1000',
  whatsappInvoices5000 = 'whatsapp-invoices-5000',
  whatsappInvoices50000 = 'whatsapp-invoices-50000',
}

export enum ItemTypes {
  Plan = 'plan',
  Addon = 'addon',
  Charge = 'charge',
}

export interface PaymentProofDTO {
  transaction: PaymentDTO[];
  merchant: { email: string };
}

export interface BillingHistoryResponseDTO {
  list: MappedBillingDocument[];
  email?: string;
  crmInfo?: {
    monshaatOrderNumber?: string;
    monshaatCompanyName?: string;
    monshaatCommercialRegistration?: string;
  };
}

export interface PartialPaymentDTO {
  isComplete: boolean;
  paymentMethod: PaymentType;
  payments: PaymentDTO[];
}

export interface PaymentScreenDTO {
  selectedPayment: PaymentType;
  payments: PaymentDTO[];
  length: number;
  hasScheduledPayment?: boolean;
  scheduledPayment?: ScheduledPaymentDTO;
}

export interface PaymentScreenDTOWithValidity extends PaymentScreenDTO {
  isValid: boolean;
}

export interface SummaryLineItem {
  itemId: string;
  itemPriceId: string;
  quantity: number;
  originalQuantity: number;
  amount: number;
  period: number;
  taxAmount: number;
  itemLevelDiscount: number;
  discountAmount: number;
  type: ItemTypeEnum;
  appliedItemLevelCoupons: string[];
  externalName?: string;
  internalName?: string;
}

export interface SummaryDTO {
  applications?: SummaryLineItem[];
  location?: SummaryLineItem;
  users?: SummaryLineItem;
  registers?: SummaryLineItem;
  fbBundle?: SummaryLineItem;
  whatsAppIntegration?: SummaryLineItem;
  fullStackAccounting?: SummaryLineItem;
  grubtech?: SummaryLineItem;
  kds?: SummaryLineItem;
  whatsappInvoices1000?: SummaryLineItem;
  whatsappInvoices5000?: SummaryLineItem;
  whatsappInvoices50000?: SummaryLineItem;
  charges?: SummaryLineItem[];
  plan?: SummaryLineItem;
  subTotal: number;
  taxAmount: number;
  creditAmount: number;
  amountPaid: number;
  total: number;
  discountCode?: string;
  discountAmount: number;
}

export type ZohoAccountInfoMapper = {
  accountID: string;
  accountOwner: string;
  additionalIdentifier?: string;
  additionalIdentifierNumber?: string;
  brandName: string;
  businessName: string;
  category?: string;
  clientTaxNumber: string;
  daysToUploadProduct?: string;
  email: string;
  employeeCount?: number;
  expectedOnboardingDate?: string;
  hasWebStore: boolean;
  isEmployee: boolean;
  needTrainingForAccounting: boolean;
  phoneNumber: string;
  reasonForUploadingProducts?: string;
  traineeContactNumber?: string;
  webStoreType?: string;
  employeeName?: string;
  employeePhone?: string;
  bestTimeToCommunicate?: string;
  monshaatOrderNumber?: string;
  monshaatCompanyName?: string;
  monshaatCommercialRegistration?: string;
  yearMonshaatServiceOffer?: string;
  clustersType?: string;
  isGrubtechEligible?: boolean;
};

export type AccountExistsResponse = {
  accountExists: boolean;
  crmAccount?: {
    crm: ZohoAccountInfoMapper;
    foundCRMAccount: boolean;
  };
  crmAccountExists?: boolean;
  subscriptionId?: string;
  data?: BillingInfo;
  accountInfo?: AccountInfo;
  isAndroidEligible?: boolean;
};

export type UpdateMerchantInfoDTO = {
  crmAccountId: string;
  vatNumber: string;
  nonTaxableEntity: boolean;
  city: string;
  districtName: string;
  buildingNumber: string;
  streetName: string;
  additionalNumber: string;
  postalCode: string;
  platformEmail: string;
  accountStatus: string;
};

export interface CheckoutFormValueType {
  planId: string;
  planDuration: number;
  locations: number;
  users: number;
  registers: number;
  fullStackAccounting: number;
  fbBundle: number;
  whatsAppIntegration: number;
  grubtech: number;
  kds: number;
  whatsappInvoices1000: number;
  whatsappInvoices5000: number;
  whatsappInvoices50000: number;
  applications: string[];
  charges: AttachedItemDTO[];
  coupons: Set<string>;
  isChargesEnabled: boolean;
  discountCode: string;
  shippingAddress?: string;
  summary: SummaryDTO;
  customizedSubscriptionStartDate?: string;
  customerNeedsGeidea: boolean;
}

export type AccountInfo = {
  id: number;
  phone: string;
  email: string;
  companyName: string;
  subscriptionID: string;
  subscriptionActive: boolean;
  subscriptionStatus: string;
};

export type BillingInfo = {
  vatNumber: string;
  city: string;
  countryCode: string;
  country?: string;
  streetName: string;
  postalCode: string;
  districtName: string;
  companyName?: string;
  additionalIdentifier?: string;
  businessCategory?: string;
  additionalIdentifierNumber?: string;
  buildingNumber?: string;
  additionalNumber?: string;
  hasWebStore?: boolean;
  webStoreType?: string;
  isRewaaPayIncluded?: boolean;
  phoneNumber?: string;
  monshaatCompanyName?: string;
  monshaatOrderNumber?: string;
  monshaatCommercialRegistration?: string;
  fullName?: string;
  mobileNumberAbsher?: string;
  nationalIdNumber?: string;
};

export type BillingInfoWithValidity = BillingInfo & {
  isValid: boolean;
};

export type BankTransferSubscriptionItems = SubscriptionItem & {
  type: string;
  basePlanId: string;
  period: number;
  price: number;
  itemLevelDiscount?: number;
  /** @testing */
  discountAmount: number;
  originalQuantity: number;
  zohoOnly?: boolean;
  firebaseOnly?: boolean;
};

export interface UpdateSubscriptionItemsDTO {
  subscriptionItems: BankTransferSubscriptionItems[];
  nowInTrial: boolean;
  checkOutFlow?: string;
  replace?: boolean;
  isAccountActive?: boolean;
  nextBillingAt?: string;
  couponIds: string[];
}

export interface UpdateSubscriptionItemsDTOV1 {
  subscriptionItems: BankTransferSubscriptionItems[];
  nowInTrial: boolean;
  checkOutFlow?: string;
  id: string;
  replace?: boolean;
  isAccountActive?: boolean;
  userEmail: string;
  nextBillingAt?: string;
  couponIds: string[];
}

export interface BankTransferDTO {
  id: string;
  userEmail: string;
  nowInTrial: boolean;
  payments: PaymentDTO[] | null;
  subscriptionItems: BankTransferSubscriptionItems[];
  amount: number;
  discountAmount: number;
  checkOutFlow: string;
  isPartial?: boolean;
  /** @testing */
  isAccountActive: boolean;
}

export interface BankTransferDTOV1 {
  id: string;
  userEmail: string;
  nowInTrial: boolean;
  invoiceURL: string | null;
  subscriptionItems: BankTransferSubscriptionItems[];
  amount: number;
  discountAmount: number;
  isPartial?: boolean;
}

export interface AdminOnlinePaymentDTOV1 extends BankTransferDTOV1 {
  paymentMethod: PaymentType;
  hardwareShippingAddress?: string;
  adminEmail: string;
}

export type RegisterNewMerchantDTO = {
  email: string;
  password: string;
  phoneNumber: string;
  companyName: string;
  hasToCheckCRM: boolean;
};

export type PlatformDetails = {
  id: number;
  businessName: string;
  email: string;
  phoneNumber: string;
  subscriptionID: string;
  subscriptionActive: boolean;
  subscriptionStatus: string;
  hasPlatformAccount: boolean;
  linkingNewAccount?: boolean;
  crmAccountExists?: boolean;
};

export type CRMDetails = {
  businessName: string;
  email: string;
  phoneNumber: string;
  accountID: number;
  accountOwner: string;
};

export interface CouponNotValidResponse {
  couponApplicabilityStatus?: CouponApplicabilityStatus;
}

export type EstimateV2Response = EstimateResponseDTO | CouponNotValidResponse;

export interface PickUpDTO {
  destinationPoint: string;
  date: Date;
  note?: string;
}

export interface DeliveryDTO {
  address: string;
  City?: string;
  Mobile?: string;
  note?: string;
}

export interface ShippingAddress {
  deliveryType: ShippingAddressTypes;
  details: DeliveryDTO | PickUpDTO;
}

export interface FeatureDTO {
  featureId: string;
  itemId: string | null;
}

export interface ReactivateTrialPayload {
  subscriptionId: string;
  newTrialEndDate: Date;
  extensionDays: number;
}

export interface InvoiceDto {
  vatNumber: string;
  city: string;
  countryCode?: string;
  country?: string;
  streetName: string;
  postalCode: string;
  districtName: string;
  companyName?: string;
  additionalIdentifier?: string;
  additionalIdentifierNumber?: string;
  buildingNumber?: string;
  additionalNumber?: string;
  monshaatOrderNumber?: string;
  monshaatCompanyName?: string;
  webhookTimestamp?: number;
  email?: string;
  customerId?: string;
  phoneNumber?: string;
}

export type InvoiceDtoWithValidity = InvoiceDto & {
  isValid: boolean;
};

export const MapAdditionalIdentifier = {
  'Commercial Registration': 'Commercial Registration',
  'Monshaat Commercial Registration': 'Monshaat Commercial Registration',
  'Freelance License': 'Freelance License',
  'Investment License': 'Investment License',
  'MLSD licenses': 'MLSD License',
  'MOMRA licenses': 'MOMRA License',
  'Municipal License': 'Municipal License',
  'Other ID': 'Other ID',
  'SAGIA licenses': 'SAGIA License',
  'MOMRA License': 'MOMRA License',
};

export interface RetryAndResubmissionPayload {
  id: string;
  type: string;
  invoiceData?: InvoiceDto;
}

export interface MappedBillingDocumentWithPaymentReceiptData
  extends MappedBillingDocument {
  lastUpdated?: number;
  txnData?: LinkedPaymentWithPaymentMethod[];
  totalCollectedAmount?: number;
  excessBalance?: number;
  printingSubtotal?: number;
  totalDiscount?: number;
  totalTaxableAmount?: number;
}

export interface LinkedPaymentWithPaymentMethod extends LinkedPayment {
  bankName?: string;
  paymentMethod: string;
}

export interface CreateCreditNoteResponseDTO {
  creditNoteId: string | undefined;
}

export interface ResetPasswordCode {
  resetPasswordCode: string;
  isValid: boolean;
}

/**
 * Frontend representation of a free device product
 * Used in checkout flow for hardware selection
 */
export interface FreeDeviceProduct {
  productCode: string;
  freeDeviceType?: string[];
}

/**
 * Hardware item already owned by the account
 */
export interface ExistingHardwareItem {
  productCode: string;
  freeDeviceType: string[];
  deviceTypeName?: string;
}

/**
 * API response from GET /admin/free-device-products/:accountId
 * Contains filtered hardware products eligible for the account and existing hardware items
 */
export interface FreeDeviceProductsResponse {
  clusterType: string | string[] | null;
  category: string | null;
  scenario: string;
  freeDeviceProducts: FreeDeviceProduct[];
  eligibleDeviceCodes: string[];
  existingHardwareItems?: ExistingHardwareItem[];
}

export class Country {
  id: number;

  countryKey: string;

  name: string;

  code: string;

  currency: string;

  language: string;

  constructor(country?) {
    Object.assign(this, country || {});
  }

  get label(): string {
    return this.name;
  }

  get value(): number {
    return this.id;
  }
}

export interface CashbackEntry {
  invoiceId: string;
  amount: number;
  reason: string;
  expiryDate?: string;
}

export interface BulkCashbackRequest {
  cashbacks: CashbackEntry[];
}

export interface CashbackTransaction {
  transactionId: string;
  tenantId: number;
  amount: number;
  sourceType: string;
  status: string;
}

export interface CashbackError {
  invoiceId: string;
  message: string;
}

export interface BulkCashbackResponse {
  transactions: CashbackTransaction[];
  errors: CashbackError[];
}
